import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, AlertCircle, CheckCircle, Sparkles } from './LordIcons';

/**
 * Enhanced Dismissible Notification Component
 * Features:
 * - Click close button to dismiss instantly
 * - Press ESC key to dismiss
 * - Pause timer on hover
 * - Visual progress bar showing auto-dismiss countdown
 * - Smooth slide-out animation
 * - Full keyboard and mouse accessibility
 * 
 * @param {Object} props
 * @param {'error' | 'success' | 'info'} props.type - Notification type
 * @param {string} props.message - Notification message
 * @param {number} [props.duration=5000] - Auto-dismiss duration in ms (0 = no auto-dismiss)
 * @param {function} [props.onDismiss] - Callback when dismissed
 * @param {string} [props.className] - Additional CSS classes
 */
const Notification = ({ 
  type = 'error', 
  message, 
  duration = 5000,
  onDismiss,
  className = ''
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [isDismissing, setIsDismissing] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [progress, setProgress] = useState(100);
  const timerRef = useRef(null);
  const notificationRef = useRef(null);
  const startTimeRef = useRef(0);
  const pauseStartRef = useRef(0);
  const pausedTotalRef = useRef(0);

  const handleDismiss = useCallback((e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (isDismissing) return;
    setIsDismissing(true);
    setTimeout(() => {
      setIsVisible(false);
      onDismiss?.();
    }, 300);
  }, [isDismissing, onDismiss]);

  // Auto-dismiss timer with progress bar
  useEffect(() => {
    if (duration === 0 || !isVisible || isDismissing) return;
    startTimeRef.current = Date.now();
    pauseStartRef.current = 0;
    pausedTotalRef.current = 0;

    const updateProgress = () => {
      if (isPaused) {
        if (!pauseStartRef.current) {
          pauseStartRef.current = Date.now();
        }
      } else if (pauseStartRef.current) {
        pausedTotalRef.current += Date.now() - pauseStartRef.current;
        pauseStartRef.current = 0;
      }

      const elapsed = Date.now() - startTimeRef.current - pausedTotalRef.current;
      const remaining = Math.max(0, duration - elapsed);
      const progressPercent = (remaining / duration) * 100;
      
      setProgress(progressPercent);

      if (remaining <= 0) {
        handleDismiss();
      } else {
        timerRef.current = setTimeout(updateProgress, 16);
      }
    };

    timerRef.current = setTimeout(updateProgress, 16);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [duration, isVisible, isDismissing, isPaused, handleDismiss]);

  // Keyboard support (ESC to dismiss)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isVisible && notificationRef.current?.contains(document.activeElement)) {
        e.preventDefault();
        handleDismiss();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isVisible, handleDismiss]);

  const handleMouseEnter = () => {
    if (duration > 0) {
      setIsPaused(true);
    }
  };

  const handleMouseLeave = () => {
    if (duration > 0) {
      setIsPaused(false);
    }
  };

  if (!isVisible) return null;

  // Define styles based on type
  const styles = {
    error: {
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200',
      textColor: 'text-red-800',
      progressColor: 'bg-red-400',
      hoverBg: 'hover:bg-red-100',
      icon: <AlertCircle className="w-5 h-5 flex-shrink-0" />
    },
    success: {
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      textColor: 'text-green-800',
      progressColor: 'bg-green-400',
      hoverBg: 'hover:bg-green-100',
      icon: <CheckCircle className="w-5 h-5 flex-shrink-0" />
    },
    info: {
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      textColor: 'text-blue-800',
      progressColor: 'bg-blue-400',
      hoverBg: 'hover:bg-blue-100',
      icon: <Sparkles className="w-5 h-5 flex-shrink-0" />
    }
  };

  const style = styles[type];

  return (
    <div
      ref={notificationRef}
      onClick={handleDismiss}
      className={`
        relative flex items-center gap-3 
        px-4 py-3 rounded-lg 
        border shadow-sm
        ${style.bgColor} 
        ${style.borderColor}
        ${style.textColor}
        ${style.hoverBg}
        transition-all duration-300 ease-out cursor-pointer group
        ${isDismissing 
          ? 'animate-out slide-out-to-right fade-out duration-300' 
          : 'animate-in slide-in-from-top fade-in duration-300 fluent-enter'
        }
        ${className}
      `}
      role="alert"
      aria-live="polite"
      aria-atomic="true"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      tabIndex="0"
    >
      {/* Icon */}
      <div className="flex-shrink-0 animate-pulse group-hover:animate-none">
        {style.icon}
      </div>

      {/* Message */}
      <p className="flex-1 text-sm font-medium pr-2">
        {message}
      </p>

      {/* Close Button */}
      <button
        onClick={handleDismiss}
        className="flex-shrink-0 p-1.5 text-current hover:opacity-100 opacity-60 transition-all rounded-md hover:bg-white/30 active:scale-95"
        aria-label="Close notification (Press ESC)"
        title="Close (ESC)"
        type="button"
      >
        <X className="w-4 h-4" />
      </button>

      {/* Progress Bar - Only show if auto-dismiss is enabled */}
      {duration > 0 && (
        <div
          className={`
            absolute bottom-0 left-0 h-1 w-full origin-left 
            ${style.progressColor}
            transition-transform duration-75 linear
            rounded-b-lg
          `}
          style={{ transform: `scaleX(${progress / 100})` }}
          role="progressbar"
          aria-valuenow={Math.round(progress)}
          aria-valuemin="0"
          aria-valuemax="100"
          aria-label={`Auto-dismiss in ${Math.round((progress / 100) * (duration / 1000))}s`}
        />
      )}
    </div>
  );
};

export default Notification;
